// @ts-strict-ignore
import {Component, inject, input, linkedSignal, OnInit, output} from "@angular/core";
import {Translations} from "@translations";
import {
	NotificationService,
	SimConfigurationService,
	SimDeviceMappingService,
	SimMaterialDialogService,
	SimRecorderService,
	SimRecordingsService,
	SimTokenService,
	SimTrainingSessionService,
	SimUserExtendedService,
	SimUtilityService,
} from "@services";
import {
	Event,
	ExternalConflict,
	Modules,
	Participant,
	ParticipantType,
	Permissions,
	Scenario,
	ScenarioRecording,
	ScenarioRecordingState,
	SessionState,
	SessionType,
	SimUser,
	TeamTrainingSession,
	TrainingRoom,
} from "@models";
import {EventEmitterService} from "@event-emitter";
import {zoomInOnEnterAnimation} from "angular-animations";
import Enumerable from "linq";
import {MatIcon} from "@angular/material/icon";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {AsyncPipe, DatePipe} from "@angular/common";
import {MatChip, MatChipListbox, MatChipOption} from "@angular/material/chips";
import {MatTooltip} from "@angular/material/tooltip";
import {MatBadge} from "@angular/material/badge";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";
import {GetTrainingRoomByIdPipe} from "@common/pipes/get-training-room-by-id.pipe";
import {FilterParticipantsPipe} from "@common/pipes/filter-participants.pipe";
import {GetScenarioRecordingStateStringForCustomerPipe} from "@common/pipes/get-scenario-recording-state-string-for-customer.pipe";
import {MatRipple} from "@angular/material/core";
import {GetRecordingMappingIdPipe} from "@common/pipes/get-recording-mapping-id.pipe";
import {GetScenarioByIdPipe} from "@common/pipes/get-scenario-by-id.pipe";
import {SimButton} from "@common/ui/button/sim-button.component";
import {TransformParticipantsToSimUsersPipe} from "@common/pipes/transform-participants-to-sim-users.pipe";
import {GetTrainingSessionStateStringForCustomerPipe} from "@common/pipes/get-training-session-state-string-for-customer.pipe";
import {routes} from "../../../../../../../environments/routes";
import {Router} from "@angular/router";
import {explicitEffect} from "ngxtension/explicit-effect";
import dayjs from "dayjs";

@Component({
	selector: "sim-team-training-session-card",
	templateUrl: "./team-training-session-card.component.html",
	styleUrls: ["./team-training-session-card.component.scss", "../../session-card.component.scss"],
	animations: [zoomInOnEnterAnimation({duration: 175})],
	providers: [EventEmitterService],
	imports: [
		MatIcon,
		MatIconButton,
		MatButton,
		StopPropagationDirective,
		MatDivider,
		DatePipe,
		GetTrainingRoomByIdPipe,
		AsyncPipe,
		GetTrainingSessionStateStringForCustomerPipe,
		MatChip,
		MatChipListbox,
		FilterParticipantsPipe,
		TransformParticipantsToSimUsersPipe,
		MatChipOption,
		MatTooltip,
		GetScenarioRecordingStateStringForCustomerPipe,
		SimButton,
		MatRipple,
		GetRecordingMappingIdPipe,
		MatBadge,
		GetScenarioByIdPipe,
		TranslateModule,
	],
})
export class TeamTrainingSessionCardComponent implements OnInit {
	readonly translate = inject(TranslateService);
	// Constants
	protected readonly T = Translations;
	protected readonly Modules = Modules;
	protected readonly Permissions = Permissions;
	protected readonly SessionType = SessionType;
	protected readonly ParticipantType = ParticipantType;
	protected readonly SessionState = SessionState;
	protected readonly ScenarioRecordingState = ScenarioRecordingState;
	protected readonly hasLicenseModule = SimConfigurationService.hasLicenseModule;
	protected readonly getRecordingStart = SimUtilityService.getRecordingStart;
	protected readonly getRecordingEnd = SimUtilityService.getRecordingEnd;
	protected readonly getScenarioRecordingProgressText = SimUtilityService.getScenarioRecordingProgressText;

	// I/O
	public readonly inputSession = input.required<TeamTrainingSession>({alias: "session"});
	public readonly creationMode = input<boolean>(false);
	public readonly showRecordingExports = input<boolean>(false);
	public readonly event = input<Event>();
	public readonly hideDraftSessions = input<boolean>();
	// TODO: rework session-card -> event-container -> event-selection prop drilling
	public readonly showOnlyRecordedScenarios = input<boolean>();
	public readonly showLoadingSpinnerOnScheduledSession = input<boolean>(false);
	public readonly usedWithinEventSelection = input<boolean>(false);
	public readonly showRemoveAction = input<boolean>(false);
	public readonly showEditAction = input<boolean>(false);
	public readonly showJoinStartActions = input<boolean>(false);
	public readonly finishedRunsClickable = input<boolean>(false);
	public readonly sessionStartable = input<boolean>(false);

	public readonly edit = output<void>();
	public readonly finishedRunClickedEmitter = output<string>();
	public readonly delete = output<void>();

	// Properties
	protected session = linkedSignal<TeamTrainingSession>(() => this.inputSession());
	public clientProperties = SimDeviceMappingService.clientProperties;
	public scenarioRecordings: ScenarioRecording[] = [];
	public participants: Participant[] = [];
	public allowedToStartAndEnter: boolean;
	public allowedToJoin: boolean;
	public allowedToJoinActive: boolean;

	readonly #trainingSessionService = inject(SimTrainingSessionService);
	readonly #recorderService = inject(SimRecorderService);
	readonly #recordingsService = inject(SimRecordingsService);
	readonly #tokenService = inject(SimTokenService);
	readonly #dialogService = inject(SimMaterialDialogService);
	readonly #router = inject(Router);
	readonly #userService = inject(SimUserExtendedService);
	readonly #eventEmitterService = inject(EventEmitterService);
	readonly #notificationService = inject(NotificationService);
	readonly #translateService = inject(TranslateService);

	ngOnInit(): void {
		this.#eventEmitterService.onNotifyExport.subscribe(async (data) => {
			const rec = this.getRecs(data.scenarioRecordingId).firstOrDefault();
			if (rec) {
				let updatedRec = await this.#recordingsService.getRecordingById(rec.id);
				rec["finishedExportsCount"] = updatedRec?.finishedExportsCount;
			}
		});

		this.#eventEmitterService.onTrainingSessionUpdated.subscribe(async (trainingSessionUpdateArgs) => {
			if (trainingSessionUpdateArgs.simSession?.id === this.session().id) {
				this.session.set(trainingSessionUpdateArgs.simSession as TeamTrainingSession);
			}
		});

		this.#eventEmitterService.onScenarioRecordingIsReadyForArchive.subscribe((data) => {
			this.getRecs(data.scenarioRecordingId).forEach((rec) => {
				rec.isReadyForArchive = true;
			});
		});

		this.#eventEmitterService.onScenarioRecordingChanged.subscribe(async (data) => {
			let updatedRec = await this.#recordingsService.getRecordingById(data.scenarioRecordingId);
			this.getRecs(data.scenarioRecordingId).forEach((rec) => {
				Object.assign(rec, updatedRec);
			});
		});

		this.#eventEmitterService.onScenarioRecordingDownloadProgressChanged.subscribe((data) => {
			this.getRecs(data.scenarioRecordingId).forEach((rec) => {
				rec.postProcessingProgress = data.progress;
			});
		});
	}

	#reactToSessionChange = explicitEffect([this.session], async () => {
		await this.getScenarioRecordings();
		this.calculatePermissions();
		this.participants = this.session().participants;
	});

	async startSessionClicked(sessionRun?: number) {
		if (!this.allowedToStartAndEnter) {
			this.joinSession();
			return;
		}

		if (
			this.clientProperties()?.isRecorder &&
			this.session().state !== SessionState.Finished &&
			(this.session().state === SessionState.Paused ||
				this.session().state === SessionState.Started ||
				this.session().state === SessionState.Scheduled)
		) {
			if (sessionRun && sessionRun !== 0) {
				const previousSessionRunsMustBeAborted = await this.#trainingSessionService.hasPreviousSessionRunsToBeAborted(
					this.session().id,
					sessionRun,
				);
				if (previousSessionRunsMustBeAborted) {
					const skipDialogResult = await this.#dialogService.openSkipSessionRunDialog({});
					if (skipDialogResult?.action === "ok" && skipDialogResult?.followUpAction === "abort-previous-session-run") {
						const abortedScenarioRecordingIds = await this.#trainingSessionService.abortPreviousSessionRuns({
							trainingSessionId: this.session().id,
							sessionRunIndex: sessionRun,
						});
						if (abortedScenarioRecordingIds.length) {
							this.#notificationService.notifySuccess("Previous Run(s) Aborted");
						}
					}
				} else {
					this.joinSessionAsOwner();
				}
			} else if (sessionRun === 0) {
				this.joinSessionAsOwner();
			}
		} else {
			//this.startSessionAsOwner();
		}
	}

	joinActiveSessionClicked() {
		if (this.session().owner.id === this.#userService.user.id) {
			this.joinSessionAsOwner();
		} else {
			this.joinSession();
		}
	}

	joinSession() {
		this.#tokenService
			.storeData({
				trainingSessionId: this.session().id,
			})
			.then((token) => {
				this.#router.navigate([routes.routes.participationView, token]);
			});
	}

	joinSessionAsOwner() {
		this.#router.navigate(["/simrecorder/default/", this.session().id]);
	}

	public startSessionAsOwner() {
		this.#recorderService
			.prepareScheduledTrainingSessionForRecording({
				trainingSessionId: this.session().id,
			})
			.then((data) => {
				if (data.success) {
					this.#notificationService.notifySuccess("Successfully started Session");
				}
			});
	}

	continueFinishedTrainingSession() {
		this.#router.navigate(["/session-selection/pro-session-selection"], {
			queryParams: {
				selectedEventId: this.event().id,
				selectedSessionId: this.session().id,
			},
			queryParamsHandling: "merge", // remove to replace all query params by provided
		});
	}

	showDetailRoom(trainingRoom: TrainingRoom) {
		this.#dialogService.openShowRoomDetailDialog(
			{
				trainingRoom: trainingRoom,
			},
			{
				disableClose: false,
				width: "1200px",
			},
		);
	}

	private conflictMessageText(conflict: ExternalConflict): string {
		const startDate = dayjs(conflict.eventData.conflictStart).format("YYYY-MM-DD HH:mm A");
		const endDate = dayjs(conflict.eventData.conflictEnd).format("YYYY-MM-DD HH:mm A");
		return startDate + " - " + endDate + " : " + conflict.eventLabel + " (" + conflict.eventResource + ")";
	}

	openShowSessionConflictsDialog(session: TeamTrainingSession) {
		let listOfConflictMessages = session.externalReference.unresolvedConflicts.map((conflict) => this.conflictMessageText(conflict));

		this.#dialogService.openListViewDialog({
			okText: this.#translateService.instant(this.T.BUTTON_OK),
			title: this.#translateService.instant(this.T.LABEL_WARNING),
			description: this.#translateService.instant(this.T.TEAM_TRAINING_SESSION_DIALOG_STEP2_SESSION_EXTERNAL_INVALID),
			myList: listOfConflictMessages,
		});
	}

	openScenarioInformationDialog(scenario: Scenario, recordingIndex: number) {
		this.#dialogService.openScenarioInformationDialog({
			scenario: scenario,
			trainingSession: this.session(),
			sessionRunIndex: recordingIndex,
		});
	}

	getScenarioRecordingById(id: string) {
		return this.#recordingsService.getRecordingById(id);
	}

	openScenarioRecordingExportDialog(scenarioRecording: ScenarioRecording) {
		this.#dialogService.openRecordingExportDialog({
			scenarioRecordingId: scenarioRecording.id,
			scenarioRecordingIdList: null,
		});
	}

	openUserCardDialog(user: SimUser) {
		this.#dialogService.openUserCardMatDialog({userData: user});
	}

	private getRecs(scenarioRecordingId: string) {
		return Enumerable.from(this.scenarioRecordings ?? []).where((x) => x.id == scenarioRecordingId);
	}

	private async getScenarioRecordings() {
		const tasks: Promise<ScenarioRecording>[] = [];
		for (const sessionRun of this.session().scenarioRecordingGroups) {
			tasks.push(this.getScenarioRecordingById(sessionRun.scenarioRecordingsMap[0]?.scenarioRecordingId));
		}
		this.scenarioRecordings = await Promise.all(tasks);

		let newParticipations: Participant[] = [];
		for (const scenarioRecording of this.scenarioRecordings) {
			newParticipations = [...newParticipations, ...(scenarioRecording?.participants ?? [])];
		}
		if (this.scenarioRecordings && this.scenarioRecordings[0]) {
			this.participants = Enumerable.from(newParticipations)
				.distinct((p) => p.user.id)
				.toArray();
		}
	}

	private calculatePermissions() {
		const participants = Enumerable.from(this.scenarioRecordings)
			.selectMany((sr) => sr?.participants ?? [])
			.concat(this.session().participants ?? []);
		const isInstructor = participants.any((participation) => participation.participantType === ParticipantType.Instructor);
		const isLearner = participants.any((participation) => participation.participantType === ParticipantType.Learner);

		const isHost = this.#userService.user.isHost;
		const isOwner = this.session().ownerUserId == this.#userService.user.id || this.event().ownerUserId == this.#userService.user.id;
		const hasPermissionToJoinAnyTrainingSession = this.#userService.hasPermission(Permissions.CanJoinAnyTrainingSession);
		const hasJoinClientPermissions =
			this.clientProperties().isTablet ||
			(this.clientProperties().isViewingStation && this.#userService.hasPermission(Permissions.CanJoinTrainingAsViewingStation));

		this.allowedToStartAndEnter =
			this.clientProperties().isRecorder &&
			(isOwner || isInstructor || isHost || this.#userService.hasPermission(Permissions.CanStartAnyVisibleSession)) &&
			this.#userService.hasPermission(Permissions.Create_Pro_Session);

		this.allowedToJoinActive = (this.clientProperties().isRecorder && isOwner) || hasJoinClientPermissions;

		this.allowedToJoin = (isOwner || isInstructor || isLearner || hasPermissionToJoinAnyTrainingSession) && hasJoinClientPermissions;
	}
}
